import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './scss/BoardMenu.scss';
import Screens from './screens/Screens';
import Unsplash from './screens/Unsplash';
import Custom from './screens/Custom';
import Archived from './screens/Archived';
import Colors from './screens/Colors';
import BoardActivity from './screens/BoardActivity';
import AIInsightsPanel from '../AI/AIInsightsPanel';
import TextToTasksParser from '../AI/TextToTasksParser';
import Close from '../icons/Close';
import KeyboardBackspace from '../icons/KeyboardBackspace';
import Info from '../icons/Info';
import Label from '../icons/Label';
import Notifications from '../icons/Notifications';
import Settings from '../icons/Settings';
import Archive from '../icons/Archive';
import History2 from '../icons/History2';
import CopyAll from '../icons/CopyAll';
import IconImage from '../icons/Image';
import IconCategory from '../icons/Category';
import IconFlashOn from '../icons/FlashOn';
import IconExtension from '../icons/Extension';
import Monitoring from '../icons/Monitoring';

const menuData = {
    menu: [
        {
            title: "Main",
            items: [
                // { icon: <Info size={16} />, label: "About this board", screen: "About", disabled: true },
                { icon: <History2 size={16} color='var(--brand-color)' />, label: "Activity", screen: "Activity", disabled: false },
                { icon: <Archive size={16} color='var(--brand-color)' />, label: "Archived items", screen: "Archived", disabled: false }
            ]
        },
        {
            title: "Intelligence",
            items: [
                { icon: <Monitoring size={16} />, label: "Analytics", screen: "Analytics", disabled: false },
                { icon: <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>psychology</span>, label: "AI Insights", screen: "AIInsights", disabled: false },
                { icon: <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chat</span>, label: "Ask AI", screen: "AIChatbot", disabled: false },
                // { icon: <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>text_snippet</span>, label: "Text to Tasks", screen: "TextToTasks", disabled: false }
            ]
        },
        {
            title: "Board Settings",
            items: [
                // { icon: <Settings size={16} />, label: "Settings", screen: "Settings", disabled: true },
                { icon: <IconImage size={16} color='var(--brand-color)' />, label: "Change background", screen: "Background", disabled: false },
                // { icon: <IconCategory size={16} />, label: "Custom Fields", screen: "CustomFields", disabled: true },
                // { icon: <IconFlashOn size={16} />, label: "Automation", screen: "Automation", disabled: true },
                // { icon: <IconExtension size={16} />, label: "Power-Ups", screen: "PowerUps", disabled: true },
                // { icon: <Label size={16} />, label: "Labels", screen: "Labels", disabled: true },
                // { icon: "emoji_objects", label: "Stickers", screen: "Stickers", disabled: true },
                // { icon: "new_window", label: "Make template", screen: "Template", disabled: true }
            ]
        },
        {
            title: "Board Utilities",
            items: [
                // { icon: <Notifications size={16} />, label: "Notify", screen: "Watch", disabled: true },
                // { icon: "mail", label: "Email-to-board", screen: "EmailToBoard", disabled: true },
                // { icon: <CopyAll size={16} />, label: "Copy board", screen: "CopyBoard", disabled: true },
                // { icon: "share", label: "Print, export, and share", screen: "Share", disabled: true },
                // { icon: <Close size={16} />, label: "Close board", screen: "CloseBoard", disabled: true }
            ]
        }
    ]
};

const BoardMenu = ({ boardId, setShowMenu, setBoard, isReadOnly = false }) => {
    const navigate = useNavigate();
    const [activeScreen, setActiveScreen] = useState(null);
    const [activeLabel, setActiveLabel] = useState('Menu');
    const [screenHistory, setScreenHistory] = useState([]); // Track screen history

    // Filter menu items based on read-only mode
    const getFilteredMenuData = () => {
        if (!isReadOnly) return menuData;

        // For read-only users, only show "Archived items"
        return {
            menu: [
                {
                    title: "Main",
                    items: menuData.menu[0].items // Keep "Archived items"
                }
                // Remove "Intelligence" and "Board Settings" sections
            ]
        };
    };

    const filteredMenuData = getFilteredMenuData();

    const handleBack = () => {
        if (screenHistory.length > 0) {
            const previousScreen = screenHistory.pop();
            setActiveScreen(previousScreen.screen);
            setActiveLabel(previousScreen.label);
            setScreenHistory([...screenHistory]); // Update history after pop
        } else {
            setActiveScreen(null); // Reset to main menu
            setActiveLabel('Menu');
        }
    };

    const handleItemClick = (screen, label) => {
        // Handle special navigation cases
        if (screen === 'Analytics') {
            navigate(`/b/${boardId}/analytics`);
            setShowMenu(false);
            return;
        }

        // Handle AIChatbot - dispatch event to toggle chatbot
        if (screen === 'AIChatbot') {
            const chatbotEvent = new CustomEvent('toggleAIChatbot');
            window.dispatchEvent(chatbotEvent);
            setShowMenu(false);
            return;
        }

        // Push current screen to history before navigating to a new screen
        setScreenHistory([...screenHistory, { screen: activeScreen, label: activeLabel }]);
        setActiveScreen(screen);
        setActiveLabel(label);
    };

    const screens = {
        About: () => <div className="screen">Coming soon</div>,
        Activity: () => <BoardActivity boardId={boardId} />,
        Archived: () => <Archived boardId={boardId} setBoard={setBoard} />,
        // AI Features
        AIInsights: () => <AIInsightsPanel boardId={boardId} />,
        TextToTasks: () => <TextToTasksParser
            boardId={boardId}
            boardTitle={setBoard?.title || 'Board'}
            onTasksCreated={(tasks) => {
                // Handle created tasks - could refresh board data
                setActiveScreen(null);
            }}
            onClose={() => setActiveScreen(null)}
        />,
        // Board Settings
        Settings: () => <div className="screen">Coming soon</div>,
        Background: () => <Screens
            activeScreen={activeScreen}
            activeLabel={activeLabel}
            screenHistory={screenHistory}
            setActiveScreen={setActiveScreen}
            setActiveLabel={setActiveLabel}
            setScreenHistory={setScreenHistory}
        />,
        Photos: () => <Unsplash />,
        Colors: () => <Colors />,
        Custom: () => <Custom />,
        CustomFields: () => <div className="screen">Coming soon</div>,
        Automation: () => <div className="screen">Coming soon</div>,
        PowerUps: () => <div className="screen">Coming soon</div>,
        Labels: () => <div className="screen">Coming soon</div>,
        Stickers: () => <div className="screen">Coming soon</div>,
        Template: () => <div className="screen">Coming soon</div>,
        Watch: () => <div className="screen">Coming soon</div>,
        EmailToBoard: () => <div className="screen">Coming soon</div>,
        CopyBoard: () => <div className="screen">Coming soon</div>,
        Share: () => <div className="screen">Coming soon</div>,
        CloseBoard: () => <div className="screen">Coming soon</div>
    };

    return (
        <div className={`menu-container`}>
            <div className={`menu-content`}>
                {!activeScreen ? (
                    <>
                        <div className="menu-screen">
                            <div className="menu-header">
                                <h3 className="menu-title">{activeLabel}</h3>
                                <button className="close-button" onClick={() => setShowMenu(false)}>
                                    <Close size={22} />
                                </button>
                            </div>
                        </div>
                        {filteredMenuData.menu.map((section, sectionIndex) => (
                            <div className={`menu-inner-content`} key={sectionIndex}>
                                <ul className="menu-section">
                                    {section.items.map((item, itemIndex) => (
                                        <li
                                            className={item.disabled ? 'menu-item deactivated' : 'menu-item'}
                                            key={itemIndex}
                                            onClick={() => handleItemClick(item.screen, item.label)}
                                        >
                                            {/* <span className="menu-icon material-symbols-outlined"></span> */}
                                            {item.icon}
                                            <span>{item.label}</span>
                                        </li>
                                    ))}
                                </ul>
                                {sectionIndex < filteredMenuData.menu.length - 1 && <div className="menu-divider"></div>}
                            </div>
                        ))}
                    </>
                ) : (
                    <div className="menu-screen">
                        <div className="menu-header">
                            <button className="back-button" onClick={handleBack}>
                                <KeyboardBackspace size={22} />
                            </button>
                            <h3 className="menu-title">{activeLabel}</h3>
                            <button className="close-button" onClick={() => setShowMenu(false)}>
                                <Close size={22} />
                            </button>
                        </div>

                        {screens[activeScreen]()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BoardMenu;
